import EventEmitter from 'eventemitter3';
import {
  ErrorCode,
  RtcError,
  ConnectionEvent,
  DTLS_STATE_UNKNOWN,
  ConnectionState,
  KIBANA_EVENT,
  PEERCONNECTION_CONNECTING_TIMEOUT,
  PEER_CONNECTION_STATE,
  loggerManager,
  Logger,
  SDP_SEMANTICS_UNIFIED_PLAN,
  SDP_SEMANTICS_PLAN_B,
  IRoom,
  getRetryCount
} from 'trtc-js-sdk-core';

import {
  isGetSendersSupported,
  isGetReceiversSupported,
  isSelectedCandidatePair,
  IS_SEI_SUPPORTED
} from 'trtc-js-sdk-core/src/common/rtc-detection';

import eventLogger from 'trtc-js-sdk-core/src/utils/log/event-log';
import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter';
import INNER_EVENT from 'trtc-js-sdk-core/src/inner-emitter/inner-event';
import { Room } from '../room';
import { ErrorName } from 'trtc-js-sdk-core/src/common/error-info';
import SignalChannel from '../signal/signal-channel';
import { generateErrorMessage } from 'trtc-js-sdk-core/src/common/error-generator';
import { ChannelEvent } from '../signal/constant';
import { performanceNow } from 'trtc-js-sdk-core/src/utils/utils';
import { IConnection } from '../type/type';
import kvReportManager, { KV_REPORT_KEY_NETWORK } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';
import { reportSuccessRate } from 'trtc-js-sdk-core/src/utils/decorators/report-success-rate';

export let isPeerConnectionConnectedBefore = false;

/**
 * Connection represents a peer connection between the local and remote
 * It handles the events received from RTCPeerConnection and events
 * received from SignalChannel which is dedicated to the connection.
 *
 * For uplink connection, it's also responsible for publishing local stream.
 * For downlink connection, it's responsible for constructing remote stream.
 * There could be two remote streams in one downlink connection, one of the two
 * is the remote stream containing the audio track and the main video track,
 * another one is the remote stream containing only the auxiliary video stream,
 * e.g, the screen sharing stream
 *
 * @export
 * @class Connection
 */
export default abstract class Connection implements IConnection {
  userId: string;
  tinyId: string;
  protected _sdpSemantics: typeof SDP_SEMANTICS_UNIFIED_PLAN | typeof SDP_SEMANTICS_PLAN_B;
  private _isUplink: boolean;
  protected _room: Room;
  protected _log: Logger;
  protected _signalChannel: SignalChannel;
  private _isErrorObserved = false;
  private _waitForPeerConnectionConnectedPromise?: Promise<void> | null;
  private _waitForPeerConnectionConnectedPromiseReject: null | ((reason?: any) => void) = null;
  protected _peerConnection: RTCPeerConnection | null = null;
  protected _emitter: EventEmitter = new EventEmitter();
  protected _currentState: ConnectionState = ConnectionState.DISCONNECTED;
  protected _isReconnecting = false;
  protected _reconnectionCount = 0;
  protected _reconnectionTimer = -1;
  // 标志是否下行首次连接
  protected _isFirstConnection = true;
  // 用于记录连接耗时
  protected _prevTime = -1;
  private _localAddress: string;
  private _remoteAddress: string;

  isDestoyed = false;
  _enableDtx?: boolean;
  constructor(options: any) {
    /*
     * the userId who initiates the connection, for uplink connection it's the local userId
     * for downlink connection, it's the remote userId.
     */
    this.userId = options.userId;
    // tinyId is the userId representation in remote server.
    this.tinyId = options.tinyId;
    /** @type {Client} */
    this._room = options.room;
    this._sdpSemantics = options.room.sdpSemantics;
    this._isUplink = options.isUplink;
    this._log = options.room.getLogger().createChild({
      id: 'n-mpc',
      userId: this._room.userId,
      remoteUserId: this.userId,
      sdkAppId: this._room.sdkAppId,
      isLocal: this._isUplink
    });
    this._signalChannel = options.signalChannel;
    this._enableDtx = options.enableDtx;
  }


  beforeConnect() {
    if (this._prevTime < 0) {
      this._prevTime = performanceNow();
    }
  }
  abstract connect(): Promise<void>;
  afterConnect() {
    try {
      if (this._isFirstConnection) {
        this._isFirstConnection = false;
        // 首次连接最大耗时上报 30s，避免数值过大影响大盘
        kvReportManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.MPC_CONNECT, cost: Math.min(performanceNow() - this._prevTime, 30 * 1000) });
      } else if (this._isReconnecting) {
        kvReportManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.MPC_RECONNECT, cost: performanceNow() - this._prevTime });
      }
      this._prevTime = -1;
    } catch (error) {
      if (this._isFirstConnection) {
        this._isFirstConnection = false;
        kvReportManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.MPC_CONNECT, error: error as Error });
      } else if (this._isReconnecting && this._reconnectionCount >= 3) {
        kvReportManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.MPC_RECONNECT, error: error as Error });
      }
      throw error;
    }
  }
  /**
   * initialize the connection, which install the permanent handlers for RTCPeerConnection and SignalChannel events
   *
   * @memberof Connection
   */
  initialize() {
    // set to |relay| if user set a TURN server to force it go through TURN,
    // otherwise use |all| for normal connection.
    // This is used only for TEST!
    // const iceTransportPolicy = isEmpty(iceServers) ? 'all' : 'relay';

    // set to |all| to allow ICE select the best candidate to connect
    // it may use host/srflx to connect if it's reachable, otherwise it
    // may use relay to connect if there's a TURN available.

    const configuration: RTCConfiguration = {
      iceServers: this._room.getIceServers(),
      iceTransportPolicy: this._room.getIceTransportPolicy(),
      // @ts-ignore
      sdpSemantics: this._sdpSemantics,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      // the two items below are non-standard & obsolete
      tcpCandidatePolicy: 'disable',
      IceTransportsType: 'nohost'
    };
    this._peerConnection = new RTCPeerConnection(configuration);
    this._peerConnection.onconnectionstatechange = this.onConnectionStateChange.bind(this);
  }

  /**
   * Close the connection and release all of its resources
   * For downlink connection, it also cleanup the remote stream
   *
   * @memberof Connection
   */
  close(reason?: string) {
    this._log.info('close connection');
    this._emitter.emit(ConnectionEvent.CLOSED, reason);

    if (this._isReconnecting) {
      this.stopReconnection();
    }

    this.closePeerConnection();
  }

  destroy() {
    this.isDestoyed = true;
  }

  /**
   * 关闭 PeerConnection
   * @param {boolean} [emitConnectionStateChanged=false] 是否要抛出 DISCONNECTED 事件。重连的时候不需要抛出。
   * @memberof Connection
   */
  closePeerConnection(emitConnectionStateChanged = false) {
    if (this._peerConnection) {
      this._log.info('close pc');
      // reset onconnectionstatechange to avoid handling |closed| event
      this._peerConnection.onconnectionstatechange = null;
      this._peerConnection.close();
      this._peerConnection = null;

      // 是否要抛出 DISCONNECTED 状态
      if (emitConnectionStateChanged) {
        this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
      }
    }
    if (this._waitForPeerConnectionConnectedPromiseReject) {
      this._waitForPeerConnectionConnectedPromiseReject(new RtcError({
        code: ErrorCode.API_CALL_ABORTED,
        message: 'connection closed'
      }));
    }
  }

  getDTLSTransportState() {
    if (!this._peerConnection) {
      return DTLS_STATE_UNKNOWN;
    }

    let transport = null;
    if (this._isUplink) {
      if (!isGetSendersSupported() || this._peerConnection.getSenders().length === 0) {
        return DTLS_STATE_UNKNOWN;
      }
      // senders 共用一个 transport 实例，取第一个即可
      transport = this._peerConnection.getSenders()[0].transport;
    } else {
      if (!isGetReceiversSupported() || this._peerConnection.getReceivers().length === 0) {
        return DTLS_STATE_UNKNOWN;
      }
      // receivers 共用一个 transport 实例，取第一个即可
      transport = this._peerConnection.getReceivers()[0].transport;
    }
    if (!transport) {
      return DTLS_STATE_UNKNOWN;
    }

    return transport.state;
  }

  onConnectionStateChange(event: any) {
    const ICEState = this._peerConnection!.iceConnectionState;
    const DTLSState = this.getDTLSTransportState();
    this._log.info(`connectionState: ${event.target.connectionState}, ICE: ${ICEState}, DTLS: ${DTLSState}`);

    if (event.target.connectionState === PEER_CONNECTION_STATE.CONNECTING) {
      this.emitConnectionStateChangedEvent(ConnectionState.CONNECTING);
    }

    // |closed| should only be fired by RTCPeerConnection.close(). In case, |closed| is fired
    // by browser internal error, we reset onconnectionstatechange callback before RTCPeerConnection.close
    // and then take both |failed| and |closed| as error
    if (
      event.target.connectionState === PEER_CONNECTION_STATE.FAILED ||
      event.target.connectionState === PEER_CONNECTION_STATE.CLOSED
    ) {
      const message = `connection ${event.target.connectionState}. ICE Transport state: ${ICEState}, DTLS Transport state: ${DTLSState}`;
      const error = new RtcError({ message, code: ErrorCode.ICE_TRANSPORT_ERROR });
      // eventLogger.logFailedEvent({
      //   userId: this._room.userId,
      //   eventType: KIBANA_EVENT.ICE_CONNECTION_STATE,
      //   error
      // });
      this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
      this.startReconnection();

      if (!this._isErrorObserved) {
        // only fire the ERROR event if error hasn't been fired before.
        this._emitter.emit(ConnectionEvent.ERROR, error);
      }
    }

    if (
      event.target.connectionState === PEER_CONNECTION_STATE.CONNECTED ||
      event.target.connectionState === PEER_CONNECTION_STATE.COMPLETED
    ) {
      this.logSelectedCandidate();
      eventLogger.logSuccessEvent({
        userId: this._room.userId,
        eventType: KIBANA_EVENT.ICE_CONNECTION_STATE
      });

      this.emitConnectionStateChangedEvent(ConnectionState.CONNECTED);
    }
  }

  // 该事件流向 Connection -> Local/Remote VideoTrack -> Stream
  emitConnectionStateChangedEvent(state: ConnectionState) {
    // 状态未变更，不抛事件
    if (state === this._currentState) {
      return false;
    }
    if (state === ConnectionState.CONNECTED) {
      // 连接成功则重置超时计数
      this._room.firewallDetector.resetTimeoutCount();
      isPeerConnectionConnectedBefore = true;
    }

    innerEmitter.emit(INNER_EVENT.PEER_CONNECTION_STATE_CHANGED, { room: this._room, prevState: this._currentState, state, remoteUserId: this._isUplink ? undefined : this.userId });
    this._emitter.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, {
      prevState: this._currentState,
      state
    });
    this._currentState = state;
    return true;
  }

  /**
   *
   * @package
   * @returns {RTCPeerConnection}
   * @memberof Connection
   */
  getPeerConnection() {
    return this._peerConnection;
  }

  getRoom() {
    return this._room;
  }

  /**
   *
   * @package
   * @returns
   * @memberof Connection
   */
  getUserId() {
    return this.userId;
  }

  /**
   *
   * @package
   */
  getTinyId() {
    return this.tinyId;
  }

  /**
   * 打印 selected candidate 相关信息
   * 使用 PeerConnection.getStats 接口获取 selected candidate。
   * @memberof Connection
   */
  async logSelectedCandidate() {
    if (!this._peerConnection) {
      return;
    }

    const stats = await this._peerConnection.getStats();
    // @ts-ignore
    for (const [, stat] of stats) {
      if (isSelectedCandidatePair(stat)) {
        // @ts-ignore
        const local = stats.get(stat.localCandidateId);
        // @ts-ignore
        const remote = stats.get(stat.remoteCandidateId);

        // firefox 及 safari 中的 candidate 地址字段通过 address 获取。
        // chrome 的 candidate 地址字段通过 ip 获取。
        // safari 浏览器拿不到 local candidate 的 ip 地址
        if (local) {
          this._log.info(`local candidate: ${local.candidateType} ${local.protocol}:${local.ip ||
            local.address}:${local.port} ${local.networkType || ''} ${local.candidateType === 'relay' ? `relayProtocol:${local.relayProtocol}` : ''
          }`);
          this._localAddress = `${local.ip || local.address}:${local.port}`;
        }
        if (remote) {
          this._log.info(`remote candidate: ${remote.candidateType} ${remote.protocol}:${remote.ip ||
            remote.address}:${remote.port}`);
          this._remoteAddress = `${remote.protocol}:${remote.ip || remote.address}`;
        }
        break;
      }
    }
  }

  getCurrentState() {
    return this._currentState;
  }

  /**
   * 等待 PeerConnection 连接成功
   * @return {Promise}
   * @memberof Connection
   */
  waitForPeerConnectionConnected() {
    // 如果多次调用该接口，则直接返回之前的 promise 实例
    if (this._waitForPeerConnectionConnectedPromise) {
      return this._waitForPeerConnectionConnectedPromise;
    }
    this._waitForPeerConnectionConnectedPromise = new Promise((resolve, reject) => {
      if (this._currentState === ConnectionState.CONNECTED) {
        return resolve();
      }
      this._waitForPeerConnectionConnectedPromiseReject = reject;
      const onConnectionStateChange = (event: any) => {
        if (event.state !== ConnectionState.CONNECTED) {
          return;
        }
        clearTimeout(timeoutId);
        uninstallEvents();
        resolve();
      };

      // 在 publish/subscribe pending 时，业务侧调用 leave 接口，可能导致 connection 连接失败
      // 此处细分该场景的错误。
      const onLeave = ({ room }: { room: IRoom }) => {
        if (room !== this._room) return;
        clearTimeout(timeoutId);
        uninstallEvents();
        reject(new RtcError({
          code: ErrorCode.API_CALL_ABORTED,
          message: generateErrorMessage({
            key: ErrorName.CONNECTION_ABORTED,
            data: 'leave room'
          })
        }));
      };

      const uninstallEvents = () => {
        innerEmitter.off(INNER_EVENT.LEAVE_SUCCESS, onLeave, this);
        this._emitter.off(ConnectionEvent.CONNECTION_STATE_CHANGED, onConnectionStateChange, this);
      };

      const timeoutId = setTimeout(() => {
        uninstallEvents();
        const error = new RtcError({
          code: ErrorCode.API_CALL_TIMEOUT,
          message: 'connection timeout'
        });
        this._room.firewallDetector.increaseTimeoutCount();
        reject(error);
      }, PEERCONNECTION_CONNECTING_TIMEOUT);

      innerEmitter.on(INNER_EVENT.LEAVE_SUCCESS, onLeave, this);
      this._emitter.on(ConnectionEvent.CONNECTION_STATE_CHANGED, onConnectionStateChange, this);
    });
    // resolved/rejected 时，清理 promise
    this._waitForPeerConnectionConnectedPromise = this._waitForPeerConnectionConnectedPromise
      .finally(() => {
        this._waitForPeerConnectionConnectedPromise = null;
        this._waitForPeerConnectionConnectedPromiseReject = null;
      });
    return this._waitForPeerConnectionConnectedPromise;
  }

  getReconnectionCount() {
    return this._reconnectionCount;
  }

  startReconnection() {
    this._isReconnecting = true;
    this.reconnect();
  }

  clearReconnectionTimer() {
    if (this._reconnectionTimer !== -1) {
      clearTimeout(this._reconnectionTimer);
      this._reconnectionTimer = -1;
    }
  }

  stopReconnection() {
    this._log.info('stop reconnection');
    this._isReconnecting = false;
    this._reconnectionCount = 0;
    this.clearReconnectionTimer();
    this._signalChannel.off(ChannelEvent.CONNECTED, this.reconnect, this);
  }

  beforeReconnect() {
    if (this._reconnectionTimer !== -1) {
      this._log.warn('reconnect() is reconnecting, ignore');
      return -1;
    }
    // 重连失败
    if (this._reconnectionCount >= getRetryCount()) {
      this._log.warn(`SDK has tried reconnect for ${this._reconnectionCount} times, but all failed, please check your network`);
      this.stopReconnection();
      const error = new RtcError({
        code: this._isUplink ? ErrorCode.UPLINK_RECONNECTION_FAILED : ErrorCode.DOWNLINK_RECONNECTION_FAILED,
        message: generateErrorMessage({ key: this._isUplink ? ErrorName.UPLINK_RECONNECTION_FAILED : ErrorName.DOWNLINK_RECONNECTION_FAILED })
      });
      // eventLogger.logFailedEvent({
      //   userId: this._room.userId,
      //   eventType: KIBANA_EVENT.UPLINK_RECONNECTION,
      //   error
      // });
      this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
      this._emitter.emit(ConnectionEvent.ERROR, error);
      return -1;
    }
    if (!this._signalChannel.isConnected) {
      this._log.warn('reconnect() signal channel is not connected, suspend reconnection until signal is connected');
      this._signalChannel.once(ChannelEvent.CONNECTED, this.reconnect, this);
      return -1;
    }

    this._reconnectionCount += 1;
    this._log.warn(`reconnect() trying [${this._reconnectionCount}]`);
    return 1;
  }
  abstract reconnect(): Promise<void>;

  /**
   * add listeners to Connection
   *
   * @param {*} event
   * @param {*} handler
   * @memberof Connection
   */
  on(event: string, handler: (...args: any[]) => void, context?: any) {
    this._emitter.on(event, handler, context);
  }

  off(event: string, handler: (...args: any[]) => void, context: any) {
    this._emitter.off(event, handler, context);
  }

  getIsReconnecting() {
    return this._isReconnecting;
  }

  get isH264() {
    return !!this._peerConnection?.remoteDescription?.sdp.includes('H264');
  }

  @reportSuccessRate(KV_REPORT_KEY_NETWORK.SET_LOCAL_DESCRIPTION, false)
  setOffer(offer: RTCSessionDescriptionInit) {
    return this._peerConnection?.setLocalDescription(offer);
  }

  @reportSuccessRate(KV_REPORT_KEY_NETWORK.SET_REMOTE_DESCRIPTION, false)
  setAnswer(answer: RTCSessionDescriptionInit) {
    return this._peerConnection?.setRemoteDescription(answer);
  }
}
